import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  Search, ShieldCheck, Crown, Briefcase, Calculator, UserCog, Users as UsersIcon,
  Mail, Phone, KeyRound, LogIn, Trash2, Save, X,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useIsAdmin } from "@/hooks/use-is-admin";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  listUsersWithEmail, updateUserAuth, sendPasswordReset,
  generateImpersonationLink, deleteAuthUser,
} from "@/lib/admin-users.functions";

export const Route = createFileRoute("/_authenticated/admin/roles")({
  component: AdminRoles,
});

type AppRole =
  | "super_admin" | "admin" | "reseller" | "accounts"
  | "manager" | "moderator" | "customer" | "user";

const ROLE_DEFS: { value: AppRole; label: string; icon: any; desc: string; superOnly?: boolean }[] = [
  { value: "super_admin", label: "Super admin", icon: Crown, desc: "Full system control. Can manage other admins.", superOnly: true },
  { value: "admin", label: "Admin", icon: ShieldCheck, desc: "Manage content, users, settings." },
  { value: "reseller", label: "Reseller", icon: Briefcase, desc: "ERP and POS access for resale partners." },
  { value: "accounts", label: "Accounts", icon: Calculator, desc: "Finance, billing, invoicing." },
  { value: "manager", label: "Manager", icon: UserCog, desc: "Operational oversight, reports." },
  { value: "moderator", label: "Moderator", icon: ShieldCheck, desc: "Moderate content and replies." },
  { value: "customer", label: "Customer", icon: UsersIcon, desc: "Tagged paying customer." },
];

type Profile = {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  phone: string | null;
};
type AuthUser = {
  id: string;
  email: string | null;
  phone: string | null;
  last_sign_in_at: string | null;
  confirmed_at: string | null;
};
type Row = Profile & {
  email: string | null;
  last_sign_in_at: string | null;
  roles: AppRole[];
};

function AdminRoles() {
  const { isSuperAdmin } = useIsAdmin();
  const fetchUsers = useServerFn(listUsersWithEmail);
  const fnUpdate = useServerFn(updateUserAuth);
  const fnReset = useServerFn(sendPasswordReset);
  const fnImpersonate = useServerFn(generateImpersonationLink);
  const fnDelete = useServerFn(deleteAuthUser);

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState<AppRole | "all" | "none">("all");
  const [editing, setEditing] = useState<Row | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Row | null>(null);


  const load = async () => {
    setLoading(true);
    try {
      const [profilesRes, rolesRes, authUsers] = await Promise.all([
        supabase.from("profiles").select("id, display_name, avatar_url, phone").order("created_at", { ascending: false }),
        supabase.from("user_roles").select("user_id, role"),
        fetchUsers(),
      ]);
      const profs = (profilesRes.data ?? []) as Profile[];
      const rmap = new Map<string, AppRole[]>();
      (rolesRes.data ?? []).forEach((r: any) => {
        rmap.set(r.user_id, [...(rmap.get(r.user_id) ?? []), r.role as AppRole]);
      });
      const amap = new Map<string, AuthUser>();
      (authUsers as AuthUser[]).forEach((u) => amap.set(u.id, u));

      // Merge: include auth users even without profile row
      const ids = new Set<string>([...profs.map((p) => p.id), ...amap.keys()]);
      const merged: Row[] = Array.from(ids).map((id) => {
        const p = profs.find((x) => x.id === id);
        const a = amap.get(id);
        return {
          id,
          display_name: p?.display_name ?? null,
          avatar_url: p?.avatar_url ?? null,
          phone: a?.phone ?? p?.phone ?? null,
          email: a?.email ?? null,
          last_sign_in_at: a?.last_sign_in_at ?? null,
          roles: rmap.get(id) ?? [],
        };
      });
      setRows(merged);
    } catch (e: any) {
      toast.error(e.message ?? "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (roleFilter === "none" && r.roles.length !== 0) return false;
      if (roleFilter !== "all" && roleFilter !== "none" && !r.roles.includes(roleFilter)) return false;
      if (!s) return true;
      return (
        (r.display_name ?? "").toLowerCase().includes(s) ||
        (r.email ?? "").toLowerCase().includes(s) ||
        (r.phone ?? "").toLowerCase().includes(s) ||
        r.id.toLowerCase().includes(s)
      );
    });
  }, [rows, q, roleFilter]);

  const roleCounts = useMemo(() => {
    const map: Record<string, number> = { all: rows.length, none: 0 };
    for (const r of rows) {
      if (r.roles.length === 0) map.none += 1;
      for (const rl of r.roles) map[rl] = (map[rl] ?? 0) + 1;
    }
    return map;
  }, [rows]);


  const toggleRole = async (userId: string, role: AppRole, has: boolean) => {
    if (role === "super_admin" && !isSuperAdmin) {
      return toast.error("Only super admins can change the super_admin role");
    }
    if (has) {
      const { error } = await supabase.from("user_roles").delete().eq("user_id", userId).eq("role", role);
      if (error) return toast.error(error.message);
      toast.success(`Revoked ${role}`);
    } else {
      const { error } = await supabase.from("user_roles").insert({ user_id: userId, role });
      if (error) return toast.error(error.message);
      toast.success(`Granted ${role}`);
    }
    await load();
  };

  const impersonate = async (email: string) => {
    if (!isSuperAdmin) return toast.error("Super admin only");
    try {
      const res = await fnImpersonate({ data: { email } });
      if (res.actionLink) {
        window.open(res.actionLink, "_blank", "noopener");
        toast.success("Sign-in link opened in new tab");
      }
    } catch (e: any) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold sm:text-xl">Users &amp; roles</h2>
          <p className="text-xs text-muted-foreground sm:text-sm">
            {rows.length} users · {filtered.length} shown
          </p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone, ID…" className="h-9 pl-8" />
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {(["all", "none", ...ROLE_DEFS.map((r) => r.value)] as (AppRole | "all" | "none")[]).map((r) => (
          <button
            key={r}
            onClick={() => setRoleFilter(r)}
            className={`rounded-full border px-2.5 py-1 text-xs transition ${
              roleFilter === r ? "border-primary bg-primary/10 text-primary" : "border-border bg-secondary/30 text-muted-foreground hover:bg-secondary"
            }`}
          >
            {r === "all" ? "All" : r === "none" ? "No role" : r}
            <span className="ml-1.5 opacity-70">{roleCounts[r] ?? 0}</span>
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <div className="w-full overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-secondary/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-medium">User</th>
              <th className="px-4 py-3 font-medium">Roles</th>
              <th className="hidden px-4 py-3 font-medium md:table-cell">Last sign-in</th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">

            {loading && (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">No users found</td></tr>
            )}
            {filtered.map((u) => {
              const initials = (u.display_name ?? u.email ?? "U").split(/[\s@]+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");
              return (
                <tr key={u.id} className="hover:bg-secondary/20">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9">
                        <AvatarImage src={u.avatar_url ?? undefined} alt="" />
                        <AvatarFallback>{initials || "U"}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="truncate font-medium">{u.display_name ?? "Unnamed"}</div>
                        <div className="truncate text-xs text-muted-foreground">{u.email ?? "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {u.roles.length === 0 && <span className="text-xs text-muted-foreground">user</span>}
                      {u.roles.map((r) => (
                        <span key={r} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          r === "super_admin" ? "bg-gradient-brand text-white" :
                          r === "admin" ? "bg-primary/10 text-primary" :
                          "border border-border bg-secondary"
                        }`}>{r}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleDateString() : "Never"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setEditing(u)}>
                        <UserCog className="mr-1 h-3.5 w-3.5" /> Manage
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>


      <EditUserSheet
        user={editing}
        open={!!editing}
        onClose={() => setEditing(null)}
        isSuperAdmin={isSuperAdmin}
        onSaved={load}
        onToggleRole={toggleRole}
        onResetPassword={async (email) => {
          try { await fnReset({ data: { email } }); toast.success("Password reset email sent"); }
          catch (e: any) { toast.error(e.message); }
        }}
        onUpdateAuth={async (userId, payload) => {
          try { await fnUpdate({ data: { userId, ...payload } }); toast.success("Account updated"); }
          catch (e: any) { toast.error(e.message); throw e; }
        }}
        onImpersonate={impersonate}
        onAskDelete={(u) => setConfirmDelete(u)}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the auth account and profile for{" "}
              <span className="font-medium">{confirmDelete?.email ?? confirmDelete?.id}</span>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (!confirmDelete) return;
                try {
                  await fnDelete({ data: { userId: confirmDelete.id } });
                  toast.success("User deleted");
                  setConfirmDelete(null);
                  setEditing(null);
                  await load();
                } catch (e: any) { toast.error(e.message); }
              }}
            >Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EditUserSheet({
  user, open, onClose, isSuperAdmin, onSaved, onToggleRole,
  onResetPassword, onUpdateAuth, onImpersonate, onAskDelete,
}: {
  user: Row | null;
  open: boolean;
  onClose: () => void;
  isSuperAdmin: boolean;
  onSaved: () => Promise<void>;
  onToggleRole: (id: string, role: AppRole, has: boolean) => Promise<unknown>;
  onResetPassword: (email: string) => Promise<unknown>;
  onUpdateAuth: (id: string, p: { email?: string; phone?: string; password?: string }) => Promise<void>;
  onImpersonate: (email: string) => Promise<unknown>;
  onAskDelete: (u: Row) => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.display_name ?? "");
      setEmail(user.email ?? "");
      setPhone(user.phone ?? "");
      setPassword("");
    }
  }, [user]);

  if (!user) return null;

  const save = async () => {
    setSaving(true);
    try {
      // Profile fields
      const profileChanged = name !== (user.display_name ?? "") || phone !== (user.phone ?? "");
      if (profileChanged) {
        const { error } = await supabase
          .from("profiles")
          .update({ display_name: name || null, phone: phone || null })
          .eq("id", user.id);
        if (error) throw error;
      }
      // Auth fields
      const payload: { email?: string; phone?: string; password?: string } = {};
      if (email && email !== (user.email ?? "")) payload.email = email;
      if (phone !== (user.phone ?? "")) payload.phone = phone;
      if (password) payload.password = password;
      if (Object.keys(payload).length) await onUpdateAuth(user.id, payload);
      toast.success("Saved");
      await onSaved();
      setPassword("");
    } catch (e: any) {
      toast.error(e.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Manage user</SheetTitle>
          <SheetDescription className="font-mono text-[11px]">{user.id}</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <section className="space-y-3">
            <h3 className="text-sm font-semibold">Details</h3>
            <div className="grid gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="name">Display name</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pw">New password (optional)</Label>
                <Input id="pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Leave blank to keep current" />
              </div>
              <Button onClick={save} disabled={saving} className="w-fit">
                <Save className="mr-1.5 h-4 w-4" /> {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold">Roles</h3>
            <div className="space-y-2 rounded-lg border border-border p-3">
              {ROLE_DEFS.map((rd) => {
                const has = user.roles.includes(rd.value);
                const disabled = rd.superOnly && !isSuperAdmin;
                const Icon = rd.icon;
                return (
                  <label key={rd.value} className={`flex cursor-pointer items-start gap-3 rounded-md p-2 hover:bg-secondary/50 ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
                    <Checkbox
                      checked={has}
                      disabled={disabled}
                      onCheckedChange={() => !disabled && onToggleRole(user.id, rd.value, has)}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5 text-sm font-medium">
                        <Icon className="h-3.5 w-3.5" /> {rd.label}
                        {rd.superOnly && <span className="rounded bg-secondary px-1.5 py-0.5 text-[9px] uppercase">super only</span>}
                      </div>
                      <div className="text-xs text-muted-foreground">{rd.desc}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold">Actions</h3>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" disabled={!user.email}
                onClick={() => user.email && onResetPassword(user.email)}>
                <KeyRound className="mr-1.5 h-4 w-4" /> Send password reset
              </Button>
              <Button variant="outline" size="sm" disabled={!user.email || !isSuperAdmin}
                onClick={() => user.email && onImpersonate(user.email)}>
                <LogIn className="mr-1.5 h-4 w-4" /> View as user
              </Button>
              <Button variant="outline" size="sm" disabled={!isSuperAdmin}
                onClick={() => onAskDelete(user)}
                className="text-destructive hover:text-destructive">
                <Trash2 className="mr-1.5 h-4 w-4" /> Delete user
              </Button>
            </div>
            {!isSuperAdmin && (
              <p className="text-xs text-muted-foreground">Impersonation and deletion require super admin.</p>
            )}
          </section>

          <Button variant="ghost" onClick={onClose} className="w-full">
            <X className="mr-1.5 h-4 w-4" /> Close
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// silence unused imports if any
void Mail; void Phone;
